import { useRef, useState } from 'react'
import { create } from '@bufbuild/protobuf'
import { timestampDate, timestampFromDate } from '@bufbuild/protobuf/wkt'
import { ArrowDown, ArrowUp, Info, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Skeleton } from 'skullet/react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ActionGate } from '@/components/shared/action-gate'
import type { Event } from '@/gen/cortado/v1/inquiries_pb'
import { EventTimelineBlockSchema, TimelineBlockKindEnum, type EventTimelineBlock } from '@/gen/cortado/v1/event_plan_pb'
import { EventTimelineSchema, type EventTimeline } from '@/gen/cortado/v1/event_timeline_pb'
import { getErrorDescription } from '@/lib/error-utils'
import {
  MAX_TIMELINE_BLOCKS,
  MAX_TIMELINE_BLOCK_MINUTES,
  blockTimeInputValue,
  describeTimelineChanges,
  draftFromBlocks,
  draftProblem,
  draftToBlockInputs,
  formatBlockDuration,
  formatBlockTime,
  isDriveKind,
  moveDraftBlock,
  newDraftBlock,
  timelineKindColor,
  withBlockTime,
  type TimelineDraftBlock,
} from '@/lib/event-timeline'
import { cn } from '@/lib/utils'
import {
  useEventTimeline,
  useRegenerateEventTimeline,
  useUpdateEventTimelineBlocks,
} from '@/stores/event-timeline.store'

interface TimelineBodyProps {
  event: Event
}

const SAMPLE_TIMELINE: EventTimeline = create(EventTimelineSchema, {
  driveTimeKnown: true,
  blocks: [
    { kind: TimelineBlockKindEnum.TIMELINE_BLOCK_KIND_PREP, label: 'Prep', minutes: 60 },
    { kind: TimelineBlockKindEnum.TIMELINE_BLOCK_KIND_DRIVE_TO, label: 'Drive to venue', minutes: 35 },
    { kind: TimelineBlockKindEnum.TIMELINE_BLOCK_KIND_SETUP, label: 'Setup', minutes: 45 },
    { kind: TimelineBlockKindEnum.TIMELINE_BLOCK_KIND_EVENT, label: 'Service', minutes: 180 },
    { kind: TimelineBlockKindEnum.TIMELINE_BLOCK_KIND_TEARDOWN, label: 'Teardown', minutes: 45 },
    { kind: TimelineBlockKindEnum.TIMELINE_BLOCK_KIND_DRIVE_TO, label: 'Drive back', minutes: 35 },
  ].map((block, i) =>
    create(EventTimelineBlockSchema, { ...block, id: `sample-${i}`, start: timestampFromDate(new Date(2026, 0, 1, 12 + i)) }),
  ),
})

function BlockRow({
  block,
  timeZone,
  driveTimeKnown,
}: {
  block: EventTimelineBlock
  timeZone?: string
  driveTimeKnown: boolean
}) {
  const start = block.start ? timestampDate(block.start) : null
  const unknownDrive = isDriveKind(block.kind) && block.minutes === 0 && !driveTimeKnown
  return (
    <li className="py-1.5">
      <div className="flex items-center gap-2">
        <div className={cn('h-6 w-1 flex-none rounded-full', timelineKindColor(block.kind))} />
        <span className="flex-1 text-xs font-medium">{block.label}</span>
        <Badge variant="secondary">{unknownDrive ? 'Unknown' : formatBlockDuration(block.minutes)}</Badge>
        <span className="w-24 text-right text-xs text-muted-foreground">
          {start ? formatBlockTime(start, timeZone) : ''}
          {block.dayOffset !== 0 && (
            <span className="ml-1 text-xs text-muted-foreground">
              {block.dayOffset > 0 ? `+${block.dayOffset}d` : `${block.dayOffset}d`}
            </span>
          )}
        </span>
      </div>
      {block.note && <div className="pl-3 text-xs text-muted-foreground">{block.note}</div>}
    </li>
  )
}

function EditRow({
  row,
  index,
  count,
  timeZone,
  onChange,
  onMove,
  onRemove,
}: {
  row: TimelineDraftBlock
  index: number
  count: number
  timeZone?: string
  onChange: (patch: Partial<TimelineDraftBlock>) => void
  onMove: (delta: number) => void
  onRemove: () => void
}) {
  const n = index + 1
  return (
    <li className="flex flex-col gap-2 rounded-md border p-2">
      <div className="flex items-center gap-2">
        <div className={cn('h-6 w-1 flex-none rounded-full', timelineKindColor(row.kind))} />
        <Input
          aria-label={`Block ${n} name`}
          value={row.label}
          maxLength={80}
          placeholder="Block name"
          onChange={(e) => onChange({ label: e.target.value })}
        />
      </div>
      <div className="flex items-center gap-2">
        <Input
          aria-label={`Block ${n} start`}
          type="time"
          value={blockTimeInputValue(row.start, timeZone)}
          onChange={(e) => onChange({ start: withBlockTime(row.start, e.target.value, timeZone) })}
        />
        <Input
          aria-label={`Block ${n} minutes`}
          type="number"
          inputMode="numeric"
          min={0}
          max={MAX_TIMELINE_BLOCK_MINUTES}
          value={Number.isNaN(row.minutes) ? '' : row.minutes}
          onChange={(e) => onChange({ minutes: e.target.value === '' ? Number.NaN : Number(e.target.value) })}
        />
        <span className="text-xs text-muted-foreground">min</span>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={`Move block ${n} up`}
          disabled={index === 0}
          onClick={() => onMove(-1)}
        >
          <ArrowUp />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={`Move block ${n} down`}
          disabled={index === count - 1}
          onClick={() => onMove(1)}
        >
          <ArrowDown />
        </Button>
        <Button type="button" variant="ghost" size="icon-sm" aria-label={`Remove block ${n}`} onClick={onRemove}>
          <Trash2 />
        </Button>
      </div>
    </li>
  )
}

function StaleNotice({
  timeline,
  organizationId,
  onRegenerate,
}: {
  timeline: EventTimeline
  organizationId: string
  onRegenerate: () => void
}) {
  const lines = describeTimelineChanges(timeline.changes)
  return (
    <Alert>
      <Info />
      <AlertTitle>Details changed since this timeline was made</AlertTitle>
      <AlertDescription>
        <ul className="list-disc pl-4">
          {lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <p>Regenerate to rebuild it from the current details.</p>
        <ActionGate organizationId={organizationId} permission="EventsManage" verb="regenerate" noun="timelines">
          <Button type="button" size="sm" variant="outline" onClick={onRegenerate}>
            <RefreshCw data-icon="inline-start" />
            Regenerate
          </Button>
        </ActionGate>
      </AlertDescription>
    </Alert>
  )
}

/**
 * The event's saved run of show: prep, drive, setup, the event, teardown and
 * the drive back, plus any blocks someone added. It is generated when the
 * quote is accepted (or on first view) and can be edited in place. When the
 * event's details change afterwards, a notice lists what changed and offers
 * Regenerate, which discards edits.
 */
export function TimelineBody({ event }: TimelineBodyProps) {
  const hasEventDate = !!event.eventDate
  const { timeline, isLoading, isError, refetch } = useEventTimeline(hasEventDate ? event.id : '')
  const { updateBlocks, isPending: isSaving } = useUpdateEventTimelineBlocks()
  const { regenerate, isPending: isRegenerating } = useRegenerateEventTimeline()
  const [draft, setDraft] = useState<TimelineDraftBlock[] | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const nextKey = useRef(0)

  const timeZone = timeline?.timezone || event.eventTimezone || undefined
  const problem = draft ? draftProblem(draft) : null

  const startEditing = () => {
    if (timeline) setDraft(draftFromBlocks(timeline.blocks))
  }

  const patchRow = (index: number, patch: Partial<TimelineDraftBlock>) =>
    setDraft((rows) => rows && rows.map((r, i) => (i === index ? { ...r, ...patch } : r)))

  const addRow = () =>
    setDraft((rows) => {
      if (!rows) return rows
      nextKey.current += 1
      const fallback = event.eventDate ? timestampDate(event.eventDate) : new Date()
      return [...rows, newDraftBlock(rows, `new-${nextKey.current}`, fallback)]
    })

  const save = async () => {
    if (!draft || problem) return
    try {
      await updateBlocks({ eventId: event.id, blocks: draftToBlockInputs(draft) })
      setDraft(null)
      toast.success('Timeline saved')
    } catch (error) {
      toast.error('Could not save the timeline', { description: getErrorDescription(error) })
    }
  }

  const confirmRegenerate = async () => {
    try {
      await regenerate({ eventId: event.id })
      setDraft(null)
      toast.success('Timeline regenerated')
    } catch (error) {
      toast.error('Could not regenerate the timeline', { description: getErrorDescription(error) })
    }
  }

  const toolbar = timeline && (
    <div className="flex min-h-8 items-center justify-between gap-2">
      {timeline.isCustomized ? <Badge variant="outline">Edited</Badge> : <span />}
      {draft ? (
        <div className="flex items-center gap-1">
          <Button type="button" size="sm" variant="ghost" onClick={() => setDraft(null)} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={save} disabled={!!problem || isSaving}>
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <ActionGate organizationId={event.organizationId} permission="EventsManage" verb="edit" noun="timelines">
            <Button type="button" size="sm" variant="ghost" onClick={startEditing}>
              <Pencil data-icon="inline-start" />
              Edit
            </Button>
          </ActionGate>
          <ActionGate organizationId={event.organizationId} permission="EventsManage" verb="regenerate" noun="timelines">
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              aria-label="Regenerate timeline"
              disabled={isRegenerating}
              onClick={() => setConfirmOpen(true)}
            >
              <RefreshCw className={cn(isRegenerating && 'animate-spin')} />
            </Button>
          </ActionGate>
        </div>
      )}
    </div>
  )

  let body
  if (!hasEventDate) {
    body = <p className="text-xs text-muted-foreground">Set the event date to build a timeline.</p>
  } else if (!isLoading && (isError || !timeline)) {
    body = (
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>Couldn't load the timeline.</span>
        <Button type="button" size="sm" variant="outline" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    )
  } else if (!isLoading && draft) {
    body = (
      <div className="flex flex-col gap-2">
        <ol className="flex flex-col gap-2">
          {draft.map((row, i) => (
            <EditRow
              key={row.key}
              row={row}
              index={i}
              count={draft.length}
              timeZone={timeZone}
              onChange={(patch) => patchRow(i, patch)}
              onMove={(delta) => setDraft((rows) => rows && moveDraftBlock(rows, i, delta))}
              onRemove={() => setDraft((rows) => rows && rows.filter((_, j) => j !== i))}
            />
          ))}
        </ol>
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={addRow}
            disabled={draft.length >= MAX_TIMELINE_BLOCKS}
          >
            <Plus data-icon="inline-start" />
            Add block
          </Button>
          {problem && (
            <span role="status" className="text-xs text-destructive">
              {problem}
            </span>
          )}
        </div>
      </div>
    )
  } else {
    body = (
      <Skeleton data={isLoading ? null : timeline} sample={SAMPLE_TIMELINE} label="Loading timeline">
        {(timeline) => (
          <div className="flex flex-col gap-3">
            {timeline.changes.length > 0 && (
              <StaleNotice
                timeline={timeline}
                organizationId={event.organizationId}
                onRegenerate={() => setConfirmOpen(true)}
              />
            )}
            {(event.baristaCount != null || event.cartCount != null) && (
              <div className="flex items-center gap-4 border-b pb-2 text-xs text-muted-foreground">
                {event.baristaCount != null && (
                  <span>
                    {event.baristaCount} barista{event.baristaCount !== 1 ? 's' : ''}
                  </span>
                )}
                {event.cartCount != null && (
                  <span>
                    {event.cartCount} cart{event.cartCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            )}
            <ol aria-label="Timeline blocks">
              {timeline.blocks.map((block, i) => (
                <BlockRow
                  key={block.id || i}
                  block={block}
                  timeZone={timeZone}
                  driveTimeKnown={timeline.driveTimeKnown}
                />
              ))}
            </ol>
            {!timeline.driveTimeKnown && (
              <p className="text-xs text-muted-foreground">
                Drive time is unknown. Add the event location and your business address to estimate it.
              </p>
            )}
          </div>
        )}
      </Skeleton>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {toolbar}
      {body}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate timeline?</AlertDialogTitle>
            <AlertDialogDescription>
              This rebuilds the timeline from the event's current details, drive time and your time settings.
              {timeline?.isCustomized && ' Your edits to this timeline will be discarded.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRegenerate}>Regenerate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
